/**
 * @file     CtaBlock.tsx
 * @purpose  Renders cta_search and cta_valuation blocks as a conversion card, with the valuation variant reading as the higher-intent one.
 */
'use client';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Link from 'next/link';
import { alpha } from '@mui/material/styles';
import { useAppTheme } from '@/core/theme/useAppTheme';
import type { ContentBlock } from '@/services/content-engine/types';

type CtaBlockProps = Extract<ContentBlock, { t: 'cta_search' | 'cta_valuation' }>;

export const CtaBlock = ({ t: kind, heading, body, label, href }: CtaBlockProps) => {
  const { theme } = useAppTheme();
  const highIntent = kind === 'cta_valuation';

  return (
    <Box
      component="aside"
      sx={{
        p: { xs: '20px', md: '28px' },
        borderRadius: `${theme.layout.radius.lg}px`,
        border: `1px solid ${highIntent ? alpha(theme.rawSemanticColors.interactivePrimary, 0.35) : theme.rawSemanticColors.borderSubtle}`,
        backgroundColor: highIntent
          ? alpha(theme.rawSemanticColors.interactivePrimary, 0.06)
          : theme.semanticColors.surfaceCard,
        display: 'flex',
        flexDirection: { xs: 'column', sm: highIntent ? 'column' : 'row' },
        alignItems: { xs: 'stretch', sm: highIntent ? 'flex-start' : 'center' },
        justifyContent: 'space-between',
        gap: { xs: '16px', sm: '24px' },
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography
          component="p"
          sx={{
            fontFamily: theme.typography.fontFamilyHeading,
            fontSize: highIntent ? theme.typography.h4 : theme.typography.bodyLg,
            fontWeight: 600,
            lineHeight: 1.25,
            color: theme.semanticColors.textPrimary,
            mb: '6px',
          }}
        >
          {heading}
        </Typography>
        <Typography
          sx={{
            fontSize: theme.typography.bodySm,
            lineHeight: 1.6,
            color: theme.semanticColors.textSecondary,
            maxWidth: '52ch',
          }}
        >
          {body}
        </Typography>
      </Box>

      <Button
        component={Link}
        href={href}
        variant={highIntent ? 'contained' : 'outlined'}
        sx={{
          flexShrink: 0,
          minHeight: 48,
          px: '22px',
          borderRadius: `${theme.layout.radius.md}px`,
          fontWeight: 600,
          textTransform: 'none',
          cursor: 'pointer',
          transition: 'transform 180ms ease, box-shadow 180ms ease',
          '&:hover': { transform: 'translateY(-1px)' },
          '@media (prefers-reduced-motion: reduce)': { transition: 'none', '&:hover': { transform: 'none' } },
        }}
      >
        {label}
      </Button>
    </Box>
  );
};
